"use client"
import { Box, Paper, Typography, Button, Alert, AlertTitle } from "@mui/material"
import { motion } from "framer-motion"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"

/**
 * A reusable error display component that shows error messages with optional retry functionality
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Error title
 * @param {string} props.message - Error message
 * @param {string|Object} props.details - Detailed error information (optional)
 * @param {Function} props.onRetry - Callback function for retry button (optional)
 * @param {string} props.retryLabel - Label for retry button (optional)
 * @param {string} props.fullPage - Whether to display as a full page error (optional)
 */
const ErrorDisplay = ({
  title = "An error occurred",
  message = "Something went wrong. Please try again later.",
  details,
  onRetry,
  retryLabel = "Try Again",
  fullPage = false,
}) => {
  const content = (
    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, 0, -5, 0],
        }}
        transition={{
          duration: 2,
          repeat: Number.POSITIVE_INFINITY,
          repeatType: "reverse",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 60, color: "error.main", mb: 2 }} />
      </motion.div>

      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {message}
      </Typography>

      {details && (
        <Alert severity="error" sx={{ mt: 2, width: "100%", textAlign: "left" }}>
          <AlertTitle>Error Details</AlertTitle>
          {typeof details === "string" ? details : JSON.stringify(details, null, 2)}
        </Alert>
      )}

      {onRetry && (
        <Button
          variant="contained"
          color="primary"
          onClick={onRetry}
          sx={{
            mt: 3,
            px: 4,
            py: 1,
            boxShadow: "0 4px 14px 0 rgba(239, 68, 68, 0.39)",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(239, 68, 68, 0.5)",
              transform: "translateY(-2px)",
            },
          }}
        >
          {retryLabel}
        </Button>
      )}
    </Box>
  )

  // If fullPage is true, render as a full page error
  if (fullPage) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 200px)",
          p: 3,
        }}
      >
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{
            p: 4,
            borderRadius: 3,
            maxWidth: 500,
            width: "100%",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.2)",
            background: "linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(254, 242, 242, 0.95) 100%)",
          }}
        >
          {content}
        </Paper>
      </Box>
    )
  }

  // Otherwise, render as a component within a page
  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        p: 4,
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(239, 68, 68, 0.2)",
        background: "linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(254, 242, 242, 0.95) 100%)",
      }}
    >
      {content}
    </Paper>
  )
}

export default ErrorDisplay

